import { localStorageUtil } from 'src/util/local-storage-util'
import { logger } from 'src/util/logger'

/**
 * Campaign attribution capture for paid traffic (e.g. Instagram ads).
 *
 * UTMs land on the landing site (grndhouse.com) and are forwarded onto the
 * "get started" links pointing at the app (see website/js/utm-forward.js), so
 * they arrive here in the app URL. We snapshot them on first load (first-touch)
 * and attach them to the GA4 signup events, so GA4 can attribute Lead /
 * CompleteRegistration to the campaign that drove them.
 *
 * First-touch: once stored, we do NOT overwrite — the campaign that originally
 * acquired the visitor wins, even if they return later via another link.
 */

const STORAGE_KEY = 'grnd_attribution'

const TRACKED_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'fbclid', 'gclid'] as const

export type AttributionParams = Partial<Record<(typeof TRACKED_PARAMS)[number], string>>

const readFromUrl = (): AttributionParams => {
  const params = new URLSearchParams(window.location.search)
  const captured: AttributionParams = {}
  TRACKED_PARAMS.forEach((key) => {
    const value = params.get(key)
    if (value) captured[key] = value
  })
  return captured
}

export const attributionUtil = {
  /**
   * Call once on app init. Stores landing params as first-touch attribution if
   * none are stored yet and the current URL carries any.
   */
  captureLandingParams: (): void => {
    try {
      const existing = localStorageUtil.getItem<AttributionParams>(STORAGE_KEY)
      if (existing?.data && Object.keys(existing.data).length > 0) return // first-touch wins

      const captured = readFromUrl()
      if (Object.keys(captured).length === 0) return

      localStorageUtil.setItem<AttributionParams>(STORAGE_KEY, captured)
      logger.debug('[Attribution] Captured landing params', captured)
    } catch (error) {
      logger.warn('[Attribution] Failed to capture landing params', error)
    }
  },

  getAttributionParams: (): AttributionParams => {
    return localStorageUtil.getItem<AttributionParams>(STORAGE_KEY)?.data ?? {}
  },
}
